import { Injectable, signal, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { Observable, Subject, throwError } from 'rxjs';
import { SessionStorageService } from '../services/session-storage.service';
import { RightBridgeApiService } from '../services/right-bridge-api.service';
import { EnvironmentService } from '../services/environment.service';
import { WarnDialogComponent } from '../warn-dialog/warn-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ModuleStatusService } from '../services/module-status.service';
import { FormService } from '../conditional-form/form-service.service';
import { NoteReview, StringResult } from '../models/general-models';
import { GroupProfile } from '../models/global.model';
@Injectable({
  providedIn: 'root',
})
export class UnifiedFlowService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private rbs = inject(RightBridgeApiService);
  private envSvc = inject(EnvironmentService);
  private dialog = inject(MatDialog);
  private modStatusSvc = inject(ModuleStatusService);
  private frmSvc = inject(FormService);

  isNaN: (value: unknown) => boolean = Number.isNaN;
  private environment;
  private org: string;
  private notesValid = {
    pp: true,
    aw: true,
    li: true,
    iw: true,
    rb: true,
    summary: true,
  };
  private steps = [];
  private saving = false;
  loading = false;
  private triggerSave = new Subject<string>();
  triggerSaveExternal = this.triggerSave.asObservable();
  private triggerValidate = new Subject<string>();
  triggerValidateExternal = this.triggerValidate.asObservable();
  private newProfile = new Subject<string>();
  newProfileExternal = this.newProfile.asObservable();
  private stepsList = new Subject<object[]>();
  stepsListExternal = this.stepsList.asObservable();
  private checkStatuses = new Subject<object[]>();
  checkStatusesExternal = this.checkStatuses.asObservable();
  private rights: string[] = [];
  private triggerTabChange = new Subject<object[]>();
  triggerTabChangeExternal = this.triggerTabChange.asObservable();
  private triggerAppChange = new Subject<object[]>();
  triggerAppChangeExternal = this.triggerAppChange.asObservable();
  formValid = signal(true);
  loadingSignal = signal(false);
  navDialog = null;
  copyCaseId = signal(null);
  copyCaseData = signal(null);
  copyFromCaseSearch = signal(false);

  constructor() {
    this.environment = this.envSvc.get();
    this.org = this.environment.org;
    this.rights = this.ss.get('rights');
  }

  getSession(): string {
    return this.rbs.getSession();
  }

  getApps(profile): Observable<GroupProfile> {
    return this.rbs.getGroupProfile(profile, 'ModuleStatus', 'rb', false);
  }

  getAllApps(): Observable<GroupProfile> {
    return this.rbs.postGroupProfile(
      'ModuleStatus',
      'rb',
      { profileId: '' },
      false
    );
  }

  getNextApp(profile, currentApp: string): Observable<GroupProfile> {
    return this.rbs.getGroupProfile(
      profile,
      `NextModule_${currentApp.toUpperCase()}`,
      'rb',
      false
    );
  }

  getPPResults(profile): Observable<GroupProfile> {
    this.rbs.setApp('pp');
    return this.rbs.getGroupProfile(profile, 'ProcessProducts', 'pp', true);
  }

  savePPSelected(profile, data): Observable<GroupProfile> {
    return this.rbs.patchGroupProfile(data, 'ProcessProducts', profile, 'pp');
  }

  summaryData(profile): Observable<GroupProfile> {
    return this.rbs.getGroupProfile(profile, 'SummaryPage', 'rb', false);
  }

  saveNote(profile, note, bridge, app): Observable<StringResult> {
    let appCode = 2311;

    switch (app.toLowerCase()) {
      case 'aw':
        appCode = 2341;
        break;
      case 'pp':
        appCode = 2311;
        break;
      case 'li':
        appCode = 2331;
        break;
      case 'iw':
        appCode = 2321;
        break;
    }

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', this.getSession())
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);
    const url = `${this.rbs.baseUrl}/Widget/SaveBridgeActions`;
    const data = {
      records: [
        {
          profileID: profile,
          bridgeID: bridge,
          actionID: appCode,
          note: note,
        },
      ],
    };

    return this.http
      .post<StringResult>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          console.error(error);
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  validateNote(
    prompt,
    bridgeVal,
    note,
    id?,
    app?: string,
    submissionCount?: number,
    maxSubmissionCount?: number,
    noteContext?: Record<string, any>
  ): Observable<NoteReview> {
    const prePrompt =
      this.ss.get('globals').GlobalsGroup.UnitAPI.NoteValidationConfig;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', this.getSession())
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    let url = `${this.rbs.baseUrl}/Widget/Chat?prePrompt=${prePrompt}`;
    if (id) {
      const profileObj = { profileid: id };
      url = url + `&configuration=${JSON.stringify(profileObj)}`;
    }
    const data = {
      ReasonText: bridgeVal,
      UserPrompt: prompt,
      SubmittedNote: note,
      App: app ?? 'rb',
      SubmissionCount: submissionCount ?? 0,
      MaxSubmissions: maxSubmissionCount ?? 0,
      ...(noteContext ?? {}),
    };

    return this.http.post<NoteReview>(url, data, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        console.error(error);
        return throwError(() => error.error || 'Server error');
      })
    );
  }

  setNoteValidity(data): void {
    if (!this.notesValid) {
      this.notesValid = {
        pp: true,
        aw: true,
        li: true,
        iw: true,
        rb: true,
        summary: true,
      };
    }
    this.notesValid[data.app] = data.valid;
  }

  getNoteValidity(app): boolean {
    if (this.notesValid) {
      return this.notesValid[app];
    }
    return true;
  }

  setLoading(data): void {
    this.loading = data;
    this.loadingSignal.set(data);
  }

  getloading(): boolean {
    return this.loading;
  }

  setSteps(data): void {
    this.steps = [...data];
    this.stepsList.next([...data]);
  }

  setSaving(val): void {
    this.saving = val;
  }

  getSaving(): boolean {
    return this.saving;
  }

  setTriggerSave(triggerSave): void {
    this.triggerSave.next(triggerSave);
  }

  setTriggerTabChange(triggerTabChange): void {
    this.triggerTabChange.next(triggerTabChange);
  }

  setTriggerAppChange(triggerAppChange): void {
    this.triggerAppChange.next(triggerAppChange);
  }

  setTriggerValidate(triggerValidate): void {
    this.triggerValidate.next(triggerValidate);
  }

  setNewProfile(profile): void {
    this.newProfile.next(profile);
  }

  setStatusCheck(): void {
    this.checkStatuses.next(null);
  }

  allowNavigation(
    stepNum = true,
    externalClick?,
    next = false,
    forceTrue = false,
    params?
  ): boolean {
    const notesActive =
      this.rights.includes('BridgeNotes') &&
      this.isNaN(+params.currentStep) &&
      (params.currentStep === 'validate' ||
        params.currentStep === 'results' ||
        params.currentStep === 'products');
    const saveActive = this.getSaving();

    if (forceTrue) {
      return true;
    } else if (
      params.currentApp === 'rb' &&
      !this.frmSvc.formValid() &&
      // params.currentStep !== '0' &&
      !this.modStatusSvc.clientInfoNotRequired()
    ) {
      this.triggerQuestionnaireError();
    } else if (
      !this.frmSvc.formValid() &&
      (+params.currentStep + 1 === params.steps.length ||
        this.isNaN(+params.requestedStep)) &&
      ((params.currentApp === 'rb' &&
        !this.modStatusSvc.clientInfoNotRequired()) ||
        params.currentApp !== 'rb') &&
      params.requestedApp !== params.currentApp
    ) {
      this.triggerQuestionnaireError();
    } else if (
      !params.prodsSelectedPP &&
      params.currentApp === 'pp' &&
      params.currentStep === 'results' &&
      next
    ) {
      this.triggerQuestionnaireError('Please Select a Product to Continue');
    } else if (
      !params.prodSelectedLW &&
      params.currentApp === 'li' &&
      params.currentStep === 'products' &&
      next
    ) {
      this.triggerQuestionnaireError('Please Select a Product to Continue');
    } else if (
      !params.progSelectedIW &&
      params.currentApp === 'iw' &&
      ((params.currentStep === 'program-selection' && (next || !stepNum)) ||
        (params.requestedStep === 'validate' && !params.iwValidateTrack))
    ) {
      this.triggerQuestionnaireError('Please Select a Program to Continue');
    }
    else if (
      params.currentApp === 'iw' &&
      params.currentStep === 'fund-allocations' &&
      (params.requestedStep === 'validate' || next) &&
      this.isFundAllocationMissing()
    ) {
      this.triggerQuestionnaireError('Please Allocate Funds to Continue');
    } else {
      if (!saveActive || this.getloading()) {
        if (notesActive) {
          if (this.notesValid[params.currentApp] || externalClick) {
            return true;
          } else {
            this.handleNotesError();
            return false;
          }
        } else {
          return true;
        }
      }
    }
    return false;
  }

  // Wizard-created funds use 'SelectedFunds.FundID'; form-grid rows use 'SelectedFunds_FundID'.
  isFundAllocationMissing(): boolean {
    const model = this.frmSvc.getModel();
    return (
      model?.['UnitSettings_RequireAllocation'] !== 'N' &&
      model?.['UnitSettings_FundSelectionType'] !== 'Advisory' &&
      !!model?.['SelectedFunds']?.every(
        fund => !fund['SelectedFunds_FundID'] && !fund['SelectedFunds.FundID']
      )
    );
  }

  triggerQuestionnaireError(
    error = 'Please Fix Questionnaire Errors to Continue'
  ): void {
    if (!this.navDialog) {
      this.setLoading(false);
      this.navDialog = this.dialog.open(WarnDialogComponent, {
        minWidth: '35vw',
        panelClass: 'warn-dialog',
        data: {
          headline: 'Warning',
          content: error,
          confirm: 'OK',
          hideCancel: true,
        },
      });
      this.navDialog.afterClosed().subscribe(() => {
        this.setLoading(false);
        this.navDialog = null;
      });
    }
  }

  handleNotesError(): void {
    let dialogRef = this.dialog.open(WarnDialogComponent, {
      minWidth: '60vw',
      panelClass: 'warn-dialog',
      data: {
        headline: 'Warning',
        content:
          '<div class="warn-text">Please enter rationale on all required items (as indicated by the "Notes Required for This Item" flag).<br/>Each required note needs to be filled out and <u>saved</u> in order to continue.</div>',
        confirm: 'OK',
        hideCancel: true,
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      dialogRef = null;
    });
  }
}
